import {
  AutoSuggestAddressResponseItems,
  FindAddressResponse,
} from '@app/finxone-web-frontend/app/lib/dtos/address.dto';
import {
  CompaniesHouseAddress,
  CompaniesHouseNumberResult,
  CompaniesHouseSearchResponse,
  CompaniesHouseSearchResult,
} from '@finxone-platform/shared/sys-config-types';
import { Observable, of } from 'rxjs';
import { MOCK_COMPANIES_HOUSE_OFFICERS } from '../../../../libs/services/ngxs-mock-data-provider/companies-house-officers.mock-data';

const WENLOCK_ROAD: CompaniesHouseAddress = {
  careOf: '',
  address1: '20-22 Wenlock Road',
  address2: '',
  country: 'GB',
  locality: 'London',
  postalCode: 'N1 7GU',
  poBox: '',
  premises: '',
  region: 'Greater London',
};

const SALT_LAKE_CITY: CompaniesHouseAddress = {
  careOf: '',
  address1: '5160 Sunnyvale Dr',
  address2: '',
  country: 'US',
  locality: 'Salt Lake City',
  postalCode: '90210',
  poBox: '',
  premises: '',
  region: 'Utah',
};

const CANARY_WHARF: CompaniesHouseAddress = {
  careOf: '',
  address1: '1 Churchill Place',
  address2: 'Canary Wharf',
  country: 'GB',
  locality: 'London',
  postalCode: 'E14 5HP',
  poBox: '',
  premises: '1',
  region: 'Greater London',
};

/**
 * The companies this mock knows about. The officer list on the first company is
 * the shared MOCK_COMPANIES_HOUSE_OFFICERS fixture so the officer-picker
 * widgets see the same people the ngxs mock data provider seeds.
 */
const MOCK_COMPANIES: CompaniesHouseNumberResult[] = [
  {
    companyName: 'ACME TRADING LIMITED',
    companyNumber: '09876543',
    companyStatus: 'active',
    dateOfCreation: '2015-11-04',
    jurisdiction: 'england-wales',
    type: 'ltd',
    undeliverableRegisteredOfficeAddress: false,
    registeredOfficeIsInDispute: false,
    registeredOfficeAddress: WENLOCK_ROAD,
    serviceAddress: WENLOCK_ROAD,
    officers: {
      activeCount: MOCK_COMPANIES_HOUSE_OFFICERS.filter((officer) => !officer.resignedOn).length,
      resignedCount: MOCK_COMPANIES_HOUSE_OFFICERS.filter((officer) => officer.resignedOn).length,
      totalResults: MOCK_COMPANIES_HOUSE_OFFICERS.length,
      items: MOCK_COMPANIES_HOUSE_OFFICERS,
    },
  },
  {
    companyName: 'FINXONE HOLDINGS LTD',
    companyNumber: '11223344',
    companyStatus: 'active',
    dateOfCreation: '2018-03-19',
    jurisdiction: 'england-wales',
    type: 'ltd',
    undeliverableRegisteredOfficeAddress: false,
    registeredOfficeIsInDispute: false,
    registeredOfficeAddress: CANARY_WHARF,
    serviceAddress: CANARY_WHARF,
    officers: {
      activeCount: 1,
      resignedCount: 0,
      totalResults: 1,
      items: [
        {
          isPrimary: true,
          name: 'MILLER, Anne',
          firstName: 'Anne',
          lastName: 'MILLER',
          email: 'anne.miller@acme.com',
          officerRole: 'director',
          appointedOn: '2018-03-19',
          nationality: 'GB',
          occupation: 'Chief Executive',
          dateOfBirth: { month: 4, year: 1982 },
          address: CANARY_WHARF,
        },
      ],
    },
  },
  {
    companyName: 'SUNNYVALE VENTURES INC',
    companyNumber: '07654321',
    companyStatus: 'active',
    dateOfCreation: '2012-06-01',
    jurisdiction: 'united-states',
    type: 'oversea-company',
    undeliverableRegisteredOfficeAddress: false,
    registeredOfficeIsInDispute: false,
    registeredOfficeAddress: SALT_LAKE_CITY,
    serviceAddress: SALT_LAKE_CITY,
    officers: {
      activeCount: 1,
      resignedCount: 0,
      totalResults: 1,
      items: [
        {
          isPrimary: true,
          name: 'TAYLOR, Brandon',
          firstName: 'Brandon',
          lastName: 'TAYLOR',
          officerRole: 'director',
          appointedOn: '2012-06-01',
          nationality: 'American',
          occupation: 'Director',
          dateOfBirth: { month: 2, year: 1978 },
          address: SALT_LAKE_CITY,
        },
      ],
    },
  },
  {
    companyName: 'WENLOCK CONSULTING LLP',
    companyNumber: '0C398210',
    companyStatus: 'dissolved',
    dateOfCreation: '2009-01-27',
    jurisdiction: 'england-wales',
    type: 'llp',
    undeliverableRegisteredOfficeAddress: false,
    registeredOfficeIsInDispute: false,
    registeredOfficeAddress: WENLOCK_ROAD,
    serviceAddress: WENLOCK_ROAD,
    officers: {
      activeCount: 0,
      resignedCount: 1,
      totalResults: 1,
      items: [
        {
          name: 'BROWN, Charlie',
          officerRole: 'member',
          appointedOn: '2009-01-27',
          resignedOn: '2019-05-30',
          nationality: 'GB',
          occupation: 'Consultant',
          dateOfBirth: { month: 9, year: 1970 },
          address: WENLOCK_ROAD,
        },
      ],
    },
  },
];

/** The addresses `autosuggestAddress` offers, keyed by the id it hands back. */
export const MOCK_ADDRESSES: Record<string, FindAddressResponse<string[]>> = {
  'GB|001': {
    postcode: 'N1 7GU',
    latitude: '51.5326',
    longitude: '-0.0928',
    formatted_address: ['20-22 Wenlock Road', '', 'London', 'Greater London', 'N1 7GU'],
    district: 'Hackney',
    county: 'Greater London',
    country: 'England',
    city: 'London',
    state: 'Greater London',
    street: 'Wenlock Road',
    property_name: '',
    property_number: '20-22',
    line_1: '20-22 Wenlock Road',
    line_2: '',
    line_3: '',
    line_4: '',
    flat_name: '',
  },
  'GB|002': {
    postcode: 'E14 5HP',
    latitude: '51.5045',
    longitude: '-0.0173',
    formatted_address: ['1 Churchill Place', 'Canary Wharf', 'London', 'Greater London', 'E14 5HP'],
    district: 'Tower Hamlets',
    county: 'Greater London',
    country: 'England',
    city: 'London',
    state: 'Greater London',
    street: 'Churchill Place',
    property_name: '',
    property_number: '1',
    line_1: '1 Churchill Place',
    line_2: 'Canary Wharf',
    line_3: '',
    line_4: '',
    flat_name: '',
  },
  'GB|003': {
    postcode: 'M2 3AE',
    latitude: '53.4795',
    longitude: '-2.2452',
    formatted_address: ['Flat 4', '82 King Street', 'Manchester', 'Greater Manchester', 'M2 3AE'],
    district: 'Manchester',
    county: 'Greater Manchester',
    country: 'England',
    city: 'Manchester',
    state: 'Greater Manchester',
    street: 'King Street',
    property_name: '',
    property_number: '82',
    line_1: 'Flat 4',
    line_2: '82 King Street',
    line_3: '',
    line_4: '',
    flat_name: 'Flat 4',
  },
  'GB|004': {
    postcode: 'EH2 2AF',
    latitude: '55.9533',
    longitude: '-3.1969',
    formatted_address: ['14 Princes Street', '', 'Edinburgh', 'City of Edinburgh', 'EH2 2AF'],
    district: 'City of Edinburgh',
    county: 'City of Edinburgh',
    country: 'Scotland',
    city: 'Edinburgh',
    state: 'City of Edinburgh',
    street: 'Princes Street',
    property_name: '',
    property_number: '14',
    line_1: '14 Princes Street',
    line_2: '',
    line_3: '',
    line_4: '',
    flat_name: '',
  },
  'US|005': {
    postcode: '90210',
    latitude: '40.7608',
    longitude: '-111.8910',
    formatted_address: ['5160 Sunnyvale Dr', '', 'Salt Lake City', 'Utah', '90210'],
    district: 'Salt Lake',
    county: 'Salt Lake',
    country: 'United States',
    city: 'Salt Lake City',
    state: 'Utah',
    street: 'Sunnyvale Dr',
    property_name: '',
    property_number: '5160',
    line_1: '5160 Sunnyvale Dr',
    line_2: '',
    line_3: '',
    line_4: '',
    flat_name: '',
  },
};

/**
 * Not made injectable so we don't accidently inject this service into main workflow angular app
 */
export class MockCompaniesHouseService {
  /**
   * GET /company-check/search/companies. The wire filters server-side on
   * companyName and location, so the mock does the same over its seed rather
   * than always answering the whole list.
   */
  searchCompanies(params: {
    companyName?: string;
    location?: string;
    includeMetadata?: boolean;
    page?: number;
    limit?: number;
  }): Observable<CompaniesHouseSearchResponse> {
    const companyName = params.companyName?.trim().toLowerCase();
    const location = params.location?.trim().toLowerCase();
    const matches = MOCK_COMPANIES.filter((company) => {
      if (companyName && !company.companyName.toLowerCase().includes(companyName)) return false;
      if (location) {
        const address = company.registeredOfficeAddress;
        const haystack = [address?.locality, address?.region, address?.postalCode, address?.country]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(location)) return false;
      }
      return true;
    });

    const page = params.page ?? 1;
    const limit = params.limit ?? 10;
    const pageStart = (page - 1) * limit;
    const result: CompaniesHouseSearchResult[] = matches
      .slice(pageStart, pageStart + limit)
      .map((company) => ({
        companyName: company.companyName,
        companyNumber: company.companyNumber,
        companyStatus: company.companyStatus,
        type: company.type,
        dateOfCreation: company.dateOfCreation,
        registeredOfficeAddress: company.registeredOfficeAddress,
      }));

    return of({
      result,
      totalRecords: matches.length,
      totalPages: Math.max(1, Math.ceil(matches.length / limit)),
      page,
      limit,
    });
  }

  /**
   * GET /company-check/:companyNumber. Unknown numbers fall back to the first
   * seeded company so a widget wired to a hard-coded number still renders.
   */
  checkCompanyNumber(companyNumber: string): Observable<CompaniesHouseNumberResult> {
    const company =
      MOCK_COMPANIES.find(
        (candidate) => candidate.companyNumber.toLowerCase() === companyNumber.trim().toLowerCase(),
      ) ?? MOCK_COMPANIES[0];
    return of(company);
  }

  /**
   * GET /address/autosuggest-address. Lives on the same mock as the company
   * lookups because both back the widget bridge's lookup operations.
   */
  autosuggestAddress(address: string): Observable<AutoSuggestAddressResponseItems> {
    const term = address?.trim().toLowerCase() ?? '';
    const suggestions = Object.entries(MOCK_ADDRESSES).map(([id, found]) => ({
      id,
      address: found.formatted_address.filter(Boolean).join(', '),
    }));
    const matches = term
      ? suggestions.filter((suggestion) => suggestion.address.toLowerCase().includes(term))
      : suggestions;
    return of(matches.length ? matches : suggestions);
  }

  /** GET /address/find-address. */
  getAddressById(id: string): Observable<FindAddressResponse<string[]>> {
    return of(MOCK_ADDRESSES[id] ?? MOCK_ADDRESSES['GB|001']);
  }
}
